import { useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@/components/IconButton';
import { DropDownProps } from '@/trio-ui/Dropdown/Dropdown.component';
import { ValuesWithLabel } from '@/types';
import TagOption from './TagOption.component';
import * as Styles from './TagSelectorDropDown.styles';
import WarningMessage from '../WarningMessage';

export type TagSelectorDropDownProps<Option> = Pick<
  DropDownProps,
  'name' | 'label' | 'onChangeDropDown' | 'isDisabled'
> & {
  filteredOptions: Option[];
  allOptions: Option[];
  tagSelectorPlaceholder: string;
  errorMessage?: string;
  disableDeleteAll?: boolean;
  isTagSelectorOpened: boolean;
  isLabelAboveField?: boolean;
  selectorLimit?: number;
  selectedOptions: string[];
  inputValue: string;
  toggleTagSelectorHandler: () => void;
  selectedOptionsHandler: (option: string) => void;
  onRemoveItem: (index: number) => void;
  removeAllOptionsHandler: () => void;
  isOptionsWithLabel: boolean;
};

export type TagSelectorOption = ValuesWithLabel<string> & {
  displayLabel?: string;
  backgroundColor?: string;
};

const isTagSelectorOption = (option: TagSelectorOption | string): option is TagSelectorOption => {
  return typeof option !== 'string';
};

const TagSelectorDropDownComponent = function <Option extends TagSelectorOption | string>({
  name,
  label,
  tagSelectorPlaceholder,
  selectorLimit,
  onChangeDropDown,
  filteredOptions,
  isDisabled,
  disableDeleteAll,
  toggleTagSelectorHandler,
  isTagSelectorOpened,
  isLabelAboveField,
  inputValue,
  selectedOptionsHandler,
  selectedOptions,
  onRemoveItem,
  removeAllOptionsHandler,
  errorMessage,
  isOptionsWithLabel,
  allOptions,
  ...props
}: TagSelectorDropDownProps<Option>): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  const getOrHideInput = function (chosenOptions: string[]): JSX.Element {
    if (!selectorLimit || chosenOptions.length < selectorLimit) {
      return (
        <Styles.InputContainer>
          {!isDisabled && (
            <IconButton
              onClick={() => {
                toggleTagSelectorHandler();
                inputRef.current?.focus();
              }}
              disabled={isDisabled}
              type="button"
              icon={AddIcon}
            />
          )}
          <Styles.Input
            ref={inputRef}
            data-testid={`${name}-enabled`}
            name={name}
            type="text"
            value={inputValue}
            disabled={isDisabled}
            autoComplete="off"
            placeholder={chosenOptions.length ? '' : tagSelectorPlaceholder}
            id="tag-selector-input"
            onFocus={toggleTagSelectorHandler}
            onChange={onChangeDropDown}
          />
        </Styles.InputContainer>
      );
    }

    return <></>;
  };

  const renderDropDownOptions = function (option: any, index): JSX.Element {
    const optionValue = option?.value ?? option;
    const optionLabel = option?.label ?? option;
    if (selectedOptions.includes(optionValue)) {
      return <Styles.HiddenOption key={`hidden-option-${name}-${index}`} />;
    }

    return (
      <Styles.OptionContainer key={`${name}-option-${index}`}>
        <Styles.DropDownOption type="button" onClick={() => selectedOptionsHandler(optionValue)}>
          {optionLabel}
        </Styles.DropDownOption>
      </Styles.OptionContainer>
    );
  };

  return (
    <Styles.TagSelector
      {...props}
      isLabelAboveField={isLabelAboveField}
      data-testid={`dropdown-${name}-btn`}
      onKeyDown={(event) => {
        if (event.keyCode === 27) {
          toggleTagSelectorHandler();
        }
      }}
    >
      <Styles.SelectorLabel htmlFor={name} isLabelAboveField={isLabelAboveField}>
        {label}
      </Styles.SelectorLabel>

      <Styles.ContentContainer id="tag-selector" isLabelAboveField={isLabelAboveField}>
        <Styles.ContentBox hasAnError={!!errorMessage}>
          {selectedOptions.length > 0 &&
            selectedOptions.map((selectedOption, index) => {
              let filteredOption = selectedOption;
              if (isOptionsWithLabel) {
                const foundOption = allOptions.find(
                  (option) =>
                    (isTagSelectorOption(option) ? option.value : option) === selectedOption
                );

                if (foundOption) {
                  filteredOption = isTagSelectorOption(foundOption)
                    ? foundOption.label
                    : foundOption;
                }
              }

              return (
                <TagOption
                  key={`${name}-option-${index.toString()}`}
                  optionLabel={filteredOption}
                  onRemoveItem={() => onRemoveItem(index)}
                  isDisabled={isDisabled}
                />
              );
            })}

          {getOrHideInput(selectedOptions)}

          {!disableDeleteAll && selectedOptions.length > 0 && (
            <Styles.RemoveButton type="button" onClick={removeAllOptionsHandler}>
              <CloseIcon sx={{ fontSize: '14px', fill: '#FFF' }} />
            </Styles.RemoveButton>
          )}
        </Styles.ContentBox>

        <Styles.DropDownContainer>
          {isTagSelectorOpened && (
            <>
              <Styles.DropDown data-testid={`${name}-options`}>
                {!filteredOptions?.length && (
                  <Styles.ErrorOption data-testid="searchQuery-error">
                    No options found
                  </Styles.ErrorOption>
                )}

                {filteredOptions?.map(renderDropDownOptions)}
              </Styles.DropDown>
            </>
          )}
        </Styles.DropDownContainer>

        <WarningMessage errorMessage={errorMessage} />
      </Styles.ContentContainer>
    </Styles.TagSelector>
  );
};

export default TagSelectorDropDownComponent;
